import { apiService } from './api';

export interface Publisher {
  id: string;
  publisherName: string;
  email?: string;
  whatsapp?: string;
  modeOfCommunication: 'EMAIL' | 'WHATSAPP';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdById: string;
  userId?: string; // Link to User account for Publisher role
  siteCount?: number;
  _count?: {
    guestBlogSites: number;
  };
}

export interface CreatePublisherRequest {
  publisherName: string;
  email?: string;
  whatsapp?: string;
  modeOfCommunication: 'EMAIL' | 'WHATSAPP';
}

export interface UpdatePublisherRequest extends Partial<CreatePublisherRequest> {
  isActive?: boolean;
}

export interface PublisherFilters {
  page?: number;
  limit?: number;
  search?: string;
  /** ALL = active + inactive; ACTIVE/INACTIVE filter by status */
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  /** @deprecated prefer `status` */
  isActive?: boolean;
}

export interface PaginatedPublishersResponse {
  data: Publisher[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

class PublisherService {
  async getPublishersWithPlacements(): Promise<Publisher[]> {
    const response = await apiService.get<ApiResponse<Publisher[]>>('/publishers/with-placements');
    return response.data;
  }

  /** Minimal publisher list for Sites page filters / add-site form (requires `sites`, not `publishers`). */
  async getPublishersForSiteWorkflow(): Promise<Publisher[]> {
    const response = await apiService.get<ApiResponse<{ publishers: Publisher[]; total: number }>>(
      '/guest-sites-api/publishers',
    );
    return response.data?.publishers ?? [];
  }

  async getPublishers(filters: PublisherFilters = {}): Promise<PaginatedPublishersResponse> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);

    let status = filters.status;
    if (!status && filters.isActive === false) status = 'INACTIVE';
    if (!status && filters.isActive === true) status = 'ACTIVE';
    if (!status) status = 'ACTIVE';
    params.append('status', status);
    
    const response = await apiService.get<PaginatedPublishersResponse>(`/publishers?${params.toString()}`);
    return {
      ...response,
      data: (response.data || []).map((publisher) => ({
        ...publisher,
        siteCount: publisher._count?.guestBlogSites ?? publisher.siteCount ?? 0,
      })),
    };
  }

  async getPublisher(id: string): Promise<Publisher> {
    const response = await apiService.get<ApiResponse<Publisher>>(`/publishers/${id}`);
    return response.data;
  }

  async createPublisher(data: CreatePublisherRequest): Promise<Publisher> {
    const response = await apiService.post<ApiResponse<Publisher>>('/publishers', data);
    return response.data;
  }

  async updatePublisher(id: string, data: UpdatePublisherRequest): Promise<Publisher> {
    const response = await apiService.put<ApiResponse<Publisher>>(`/publishers/${id}`, data);
    return response.data;
  }

  async deletePublisher(id: string): Promise<void> {
    await apiService.delete<ApiResponse<void>>(`/publishers/${id}`);
  }

  async togglePublisherStatus(id: string): Promise<Publisher> {
    const response = await apiService.patch<ApiResponse<Publisher>>(`/publishers/${id}/toggle-status`);
    return response.data;
  }

  async getPublisherByEmail(email: string): Promise<Publisher> {
    const encodedEmail = encodeURIComponent(email);
    const response = await apiService.get<ApiResponse<Publisher>>(`/publishers/by-email/${encodedEmail}`);
    return response.data;
  }

  // Get publisher earnings (for current publisher)
  async getPublisherEarnings(): Promise<{ totalEarnings: number; completedPlacements: number; totalPlacements: number }> {
    const response = await apiService.get<{ success: boolean; data: { totalEarnings: number; completedPlacements: number; totalPlacements: number } }>('/publisher-earnings');
    return response.data;
  }

  // Get all publisher earnings (for Super Admin)
  async getAllPublisherEarnings(): Promise<Array<{ publisherId: string; publisherName: string; totalEarnings: number; completedPlacements: number }>> {
    const response = await apiService.get<{ success: boolean; data: Array<{ publisherId: string; publisherName: string; totalEarnings: number; completedPlacements: number }> }>('/publisher-earnings/all');
    return response.data;
  }
}

export const publisherService = new PublisherService();
